// Persistent store for the active Proposal Review package (scoped per user, so it
// survives refresh and opens automatically when proposals are ready). Applying
// items reuses the existing applyProposal() — no new calendar logic.

import { scopedKey } from "./userScope";
import { applyProposal } from "./applyProposals";
import { itemToProposal, STATUS } from "./proposalPipeline";

const KEY = () => scopedKey("proposalPackage.v1");

export function getActivePackage() {
  try {
    const raw = localStorage.getItem(KEY());
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setActivePackage(pkg) {
  if (!pkg) localStorage.removeItem(KEY());
  else localStorage.setItem(KEY(), JSON.stringify(pkg));
  window.dispatchEvent(new CustomEvent("chronicles:package-changed"));
}

export function clearActivePackage() {
  localStorage.removeItem(KEY());
  window.dispatchEvent(new CustomEvent("chronicles:package-changed"));
}

// Apply a set of items in safe batches. One failing item is isolated and the
// rest continue; successful work is never discarded. Returns { applied, failed, failures }.
//
// Same-batch parent/child linking: when the AI creates BOTH a parent and its child
// in one package, the parent create carries a temporary `new.tempId` and the child's
// `new.parentEventId` equals that tempId. Children referencing a not-yet-created
// parent are DEFERRED until after the parent create runs, then linked to the parent's
// real id. If the parent create failed, the invalid reference is dropped (the child is
// saved as an independent event) rather than persisting a broken link.
export async function applyItems(items, { batchSize = 50, onProgress } = {}) {
  let applied = 0;
  const failures = [];
  const tempMap = {}; // tempId -> real created id
  const tempTokens = new Set((items || []).map((it) => it && it.new && it.new.tempId).filter(Boolean));
  const deferred = [];

  const applyOne = (it) => {
    // Stamp a deterministic sourceEventId on create items so a retry / refresh /
    // re-approval of the SAME package can never duplicate an already-created event
    // (applyProposal skips a create whose sourceEventId already exists).
    let item = it;
    if (item && item.kind === "create" && item.new && !item.new.sourceEventId && item.id) {
      item = { ...item, new: { ...item.new, sourceEventId: `pi_${item.id}` } };
    }
    try {
      const res = applyProposal(itemToProposal(item));
      if (res.ok) {
        applied += 1;
        const tid = item && item.new && item.new.tempId;
        if (tid && res.id) tempMap[tid] = res.id;
      } else {
        failures.push({ id: item.id, reason: res.message });
      }
    } catch (e) {
      failures.push({ id: item.id, reason: String((e && e.message) || e) });
    }
  };

  const total = items.length;
  let processed = 0;
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    for (const it of batch) {
      const pid = it && it.new && it.new.parentEventId;
      // Child referencing a same-batch parent that isn't created yet → defer.
      if (pid && tempTokens.has(pid) && !tempMap[pid]) {
        deferred.push(it);
        processed += 1;
        continue;
      }
      // Child referencing a same-batch parent already created → swap to real id.
      if (pid && tempTokens.has(pid) && tempMap[pid]) {
        applyOne({ ...it, new: { ...it.new, parentEventId: tempMap[pid] } });
      } else {
        applyOne(it);
      }
      processed += 1;
    }
    onProgress?.({ applied, failed: failures.length, processed, total });
    await new Promise((r) => setTimeout(r, 0));
  }

  // Resolve deferred children now that all parent creates have run.
  for (const it of deferred) {
    const pid = it.new.parentEventId;
    const real = tempMap[pid] || null; // null → parent creation failed; drop invalid ref
    applyOne({ ...it, new: { ...it.new, parentEventId: real } });
  }
  if (deferred.length) onProgress?.({ applied, failed: failures.length, processed: total, total });

  return { applied, failed: failures.length, failures };
}

// Items that are eligible to be applied: not rejected, not needing clarification,
// and NOT execution-blocked (black conflicts can never become executable).
export function approvableItems(pkg) {
  if (!pkg) return [];
  return pkg.items.filter(
    (it) => it.status !== STATUS.REJECTED && it.status !== STATUS.CLARIFY && it.status !== STATUS.BLOCKED
  );
}
